/**
 * Svelte action: vertical auto-scroll for overflowing text.
 * Uses scrollTop animation — no DOM wrapper manipulation needed.
 * All instances synchronize their scroll cycles via a shared coordinator.
 *
 * Usage: <div use:autoScrollY>...</div>
 *        <p use:autoScrollY={{ group: 'items' }}>...</p>
 *
 * @param {HTMLElement} node - The container element (must have constrained height + overflow:hidden)
 * @param {Object} [params] - Configuration
 * @param {string} [params.group='default'] - Sync group name (instances in the same group animate together)
 */

// --- Shared coordinator ---
const groups = new Map();

const SPEED = 15; // px/sec
const PAUSE_MS = 3500; // pause at each end
const MIN_OVERFLOW = 5; // px — ignore tiny overflow (e.g. 3-line items that barely fit)

function getGroup(name) {
    if (!groups.has(name)) {
        groups.set(name, { instances: new Set(), timers: [], running: false });
    }
    return groups.get(name);
}

function clearGroupTimers(group) {
    group.timers.forEach(clearTimeout);
    group.timers = [];
}

function joinGroup(instance, groupName) {
    const group = getGroup(groupName);
    group.instances.add(instance);
    if (!group.running) {
        group.running = true;
        scheduleGroupCycle(group);
    }
}

function leaveGroup(instance, groupName) {
    if (!groups.has(groupName)) return;
    const group = groups.get(groupName);
    group.instances.delete(instance);
    if (group.instances.size === 0) {
        clearGroupTimers(group);
        group.running = false;
        groups.delete(groupName);
    }
}

function scheduleGroupCycle(group) {
    const t = setTimeout(() => runGroupCycle(group), PAUSE_MS);
    group.timers.push(t);
}

function runGroupCycle(group) {
    if (group.instances.size === 0) {
        group.running = false;
        return;
    }

    // Find the max overflow to determine shared scroll duration
    let maxOverflow = 0;
    for (const inst of group.instances) {
        if (!inst.isPaused) {
            maxOverflow = Math.max(maxOverflow, inst.overflow);
        }
    }

    if (maxOverflow <= 0) {
        scheduleGroupCycle(group);
        return;
    }

    const scrollMs = (maxOverflow / SPEED) * 1000;

    // Phase 1: scroll to end
    for (const inst of group.instances) {
        inst.scrollToEnd(scrollMs);
    }

    // After scroll + pause: scroll back to start
    const t1 = setTimeout(() => {
        for (const inst of group.instances) {
            inst.scrollToStart(scrollMs);
        }
        // After scroll back + pause: repeat
        const t2 = setTimeout(() => runGroupCycle(group), scrollMs + PAUSE_MS);
        group.timers.push(t2);
    }, scrollMs + PAUSE_MS);
    group.timers.push(t1);
}

// --- Easing ---
function easeInOut(t) {
    return 0.5 - 0.5 * Math.cos(Math.PI * t);
}

// --- Action ---
export function autoScrollY(node, params = {}) {
    const groupName = params?.group || 'default';

    let isActive = false;
    let isPaused = false;
    let overflow = 0;
    let originalAlignItems = '';
    let originalJustifyContent = '';
    let animFrameId = null;

    const instance = {
        get isPaused() { return isPaused; },
        get overflow() { return overflow; },
        scrollToEnd(durationMs) {
            if (isPaused || overflow <= 0) return;
            animateScrollTop(overflow, durationMs);
        },
        scrollToStart(durationMs) {
            if (isPaused) return;
            animateScrollTop(0, durationMs);
        },
    };

    function animateScrollTop(target, durationMs) {
        if (animFrameId) cancelAnimationFrame(animFrameId);
        const start = node.scrollTop;
        const distance = target - start;
        if (Math.abs(distance) < 1) return;
        const startTime = performance.now();

        function step(now) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / durationMs, 1);
            node.scrollTop = start + distance * easeInOut(progress);
            if (progress < 1) {
                animFrameId = requestAnimationFrame(step);
            } else {
                animFrameId = null;
            }
        }
        animFrameId = requestAnimationFrame(step);
    }

    function activate() {
        if (isActive) return;
        isActive = true;
        // Override flex centering so content starts from top for predictable scrolling
        originalAlignItems = node.style.alignItems;
        originalJustifyContent = node.style.justifyContent;
        node.style.alignItems = 'flex-start';
        node.style.justifyContent = 'flex-start';
        node.style.overflow = 'hidden';
        node.scrollTop = 0;
        // Re-measure after layout change — centered flex splits overflow top/bottom,
        // but flex-start puts it all at the bottom where scrollTop can reach it
        overflow = Math.max(0, node.scrollHeight - node.clientHeight);
        joinGroup(instance, groupName);
    }

    function deactivate() {
        if (!isActive) return;
        leaveGroup(instance, groupName);
        isActive = false;
        isPaused = false;
        overflow = 0;
        if (animFrameId) {
            cancelAnimationFrame(animFrameId);
            animFrameId = null;
        }
        node.style.alignItems = originalAlignItems;
        node.style.justifyContent = originalJustifyContent;
        node.scrollTop = 0;
    }

    function checkOverflow() {
        const measured = node.scrollHeight - node.clientHeight;
        if (measured > MIN_OVERFLOW) {
            overflow = measured;
            if (!isActive) activate();
        } else {
            overflow = 0;
            if (isActive) deactivate();
        }
    }

    function pause() {
        if (!isActive || isPaused) return;
        isPaused = true;
        if (animFrameId) {
            cancelAnimationFrame(animFrameId);
            animFrameId = null;
        }
    }

    function resume() {
        if (!isActive || !isPaused) return;
        isPaused = false;
    }

    // Event listeners
    node.addEventListener('mouseenter', pause);
    node.addEventListener('mouseleave', resume);
    node.addEventListener('touchstart', pause, { passive: true });
    node.addEventListener('touchend', resume);
    // iOS fires touchcancel (not touchend) when the system claims the gesture;
    // without this the marquee stays paused forever, stranding text mid-scroll.
    node.addEventListener('touchcancel', resume);

    // Observers — scrollTop changes don't trigger these, so no infinite loop
    // ResizeObserver is unavailable on some old/embedded WebViews; degrade gracefully
    // (initial check + MutationObserver still run) instead of throwing during render.
    const resizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => checkOverflow())
        : null;
    resizeObserver?.observe(node);

    const mutationObserver = new MutationObserver(() => {
        if (isActive) deactivate();
        checkOverflow();
    });
    mutationObserver.observe(node, { childList: true, characterData: true, subtree: true });

    // Initial check
    checkOverflow();

    return {
        destroy() {
            if (isActive) leaveGroup(instance, groupName);
            if (animFrameId) cancelAnimationFrame(animFrameId);
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
            node.removeEventListener('mouseenter', pause);
            node.removeEventListener('mouseleave', resume);
            node.removeEventListener('touchstart', pause);
            node.removeEventListener('touchend', resume);
            node.removeEventListener('touchcancel', resume);
            node.style.alignItems = originalAlignItems;
            node.style.justifyContent = originalJustifyContent;
            node.scrollTop = 0;
        }
    };
}
